import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Send, Wrench, Paintbrush, Box } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceTypes = [
{ value: "custom_builds", label: "Custom Builds" },
{ value: "specialty_coatings", label: "Specialty Coatings" },
{ value: "custom_paint", label: "Custom Paint" },
{ value: "metal_fabrication", label: "Metal Fabrication & Custom Bodywork" },
{ value: "3d_design", label: "3D Design & Parts Fabrication" },
{ value: "other", label: "Other" }];



export default function QuotePage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    vehicle_year: "",
    vehicle_make: "",
    vehicle_model: "",
    service_type: "",
    description: ""
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleServiceChange = (value) => {
    setFormData({ ...formData, service_type: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.service_type) {
      toast.error("Please select a service type");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/quotes`, formData);
      toast.success("Thanks for reaching out! We'll be in touch within 24-48 hours to discuss your project.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        vehicle_year: "",
        vehicle_make: "",
        vehicle_model: "",
        service_type: "",
        description: ""
      });
    } catch (error) {
      toast.error("Failed to submit quote request. Please try again.");
      console.error("Error submitting quote:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="quote-page">
      {/* Header */}
      <section className="py-24 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl">

            <span className="font-mono text-xs tracking-[0.3em] text-amber-500 uppercase mb-4 block">
              START YOUR PROJECT
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl text-white tracking-tighter mb-6">
              LET'S GET<br />
              <span className="text-amber-500">STARTED</span>
            </h1>
            <p className="font-body text-lg text-zinc-300 leading-relaxed">
              Tell us about your project and let's start the conversation. We'll reach out to discuss the details and figure out the best path forward together.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-16 bg-[#09090b]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

            <div className="bg-zinc-900 border border-white/10 p-8 md:p-12">
              {/* Service Type Selection Header */}
              <div className="mb-10">
                <h2 className="font-heading text-2xl text-white mb-6">WHAT CAN WE HELP YOU WITH?</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Link
                    to="/services"
                    className="flex items-center gap-4 p-4 border border-white/10 hover:border-amber-500/30 transition-colors group">

                    <Wrench size={24} className="text-amber-500 flex-shrink-0" />
                    <div>
                      <h4 className="font-heading text-lg text-white group-hover:text-amber-500 transition-colors">CUSTOM BUILDS</h4>
                      <p className="font-mono text-xs text-zinc-500">Ground up, custom builds</p>
                    </div>
                  </Link>
                  <Link
                    to="/services"
                    className="flex items-center gap-4 p-4 border border-white/10 hover:border-amber-500/30 transition-colors group">

                    <Paintbrush size={24} className="text-amber-500 flex-shrink-0" />
                    <div>
                      <h4 className="font-heading text-lg text-white group-hover:text-amber-500 transition-colors">COATINGS</h4>
                      <p className="font-mono text-xs text-zinc-500">Cerakote®, custom paint, high-temp</p>
                    </div>
                  </Link>
                  <Link
                    to="/services"
                    className="flex items-center gap-4 p-4 border border-white/10 hover:border-amber-500/30 transition-colors group">

                    <Box size={24} className="text-amber-500 flex-shrink-0" />
                    <div>
                      <h4 className="font-heading text-lg text-white group-hover:text-amber-500 transition-colors">3D DESIGN & PARTS FAB</h4>
                      <p className="font-mono text-xs text-zinc-500">Sketch or scan to finished part</p>
                    </div>
                  </Link>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8" data-testid="quote-form">
                {/* Personal Info */}
                <div>
                  <h3 className="font-heading text-xl text-amber-500 mb-4">YOUR INFORMATION</h3>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="font-mono text-xs tracking-wide text-zinc-400 uppercase">
                        FULL NAME *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="mt-2 bg-zinc-950 border-white/10 text-white rounded-none focus:border-amber-500 h-12"
                        placeholder="John Doe"
                        data-testid="quote-name-input" />

                    </div>
                    <div>
                      <Label htmlFor="email" className="font-mono text-xs tracking-wide text-zinc-400 uppercase">
                        EMAIL *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="mt-2 bg-zinc-950 border-white/10 text-white rounded-none focus:border-amber-500 h-12"
                        placeholder="john@example.com"
                        data-testid="quote-email-input" />

                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="phone" className="font-mono text-xs tracking-wide text-zinc-400 uppercase">
                        PHONE NUMBER
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-2 bg-zinc-950 border-white/10 text-white rounded-none focus:border-amber-500 h-12"
                        placeholder="(555) 123-4567"
                        data-testid="quote-phone-input" />

                    </div>
                  </div>
                </div>

                {/* Vehicle Info */}
                <div>
                  <h3 className="font-heading text-xl text-amber-500 mb-4">VEHICLE INFORMATION</h3>
                  <div className="grid sm:grid-cols-3 gap-6">
                    <div>
                      <Label htmlFor="vehicle_year" className="font-mono text-xs tracking-wide text-zinc-400 uppercase">
                        YEAR
                      </Label>
                      <Input
                        id="vehicle_year"
                        name="vehicle_year"
                        type="text"
                        value={formData.vehicle_year}
                        onChange={handleChange}
                        className="mt-2 bg-zinc-950 border-white/10 text-white rounded-none focus:border-amber-500 h-12"
                        placeholder="1969"
                        data-testid="quote-year-input" />

                    </div>
                    <div>
                      <Label htmlFor="vehicle_make" className="font-mono text-xs tracking-wide text-zinc-400 uppercase">
                        MAKE
                      </Label>
                      <Input
                        id="vehicle_make"
                        name="vehicle_make"
                        type="text"
                        value={formData.vehicle_make}
                        onChange={handleChange}
                        className="mt-2 bg-zinc-950 border-white/10 text-white rounded-none focus:border-amber-500 h-12"
                        placeholder="Chevrolet"
                        data-testid="quote-make-input" />

                    </div>
                    <div>
                      <Label htmlFor="vehicle_model" className="font-mono text-xs tracking-wide text-zinc-400 uppercase">
                        MODEL
                      </Label>
                      <Input
                        id="vehicle_model"
                        name="vehicle_model"
                        type="text"
                        value={formData.vehicle_model}
                        onChange={handleChange}
                        className="mt-2 bg-zinc-950 border-white/10 text-white rounded-none focus:border-amber-500 h-12"
                        placeholder="Camaro SS"
                        data-testid="quote-model-input" />

                    </div>
                  </div>
                </div>

                {/* Service Type */}
                <div>
                  <h3 className="font-heading text-xl text-amber-500 mb-4">PROJECT DETAILS</h3>
                  <div className="space-y-6">
                    <div>
                      <Label className="font-mono text-xs tracking-wide text-zinc-400 uppercase">
                        SERVICE TYPE *
                      </Label>
                      <Select onValueChange={handleServiceChange} value={formData.service_type}>
                        <SelectTrigger
                          className="mt-2 bg-zinc-950 border-white/10 text-white rounded-none focus:border-amber-500 h-12"
                          data-testid="quote-service-select">

                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-white/10">
                          {serviceTypes.map((type) =>
                          <SelectItem
                            key={type.value}
                            value={type.value}
                            className="text-white hover:bg-zinc-800 focus:bg-zinc-800">

                              {type.label}
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="description" className="font-mono text-xs tracking-wide text-zinc-400 uppercase">
                        PROJECT DESCRIPTION *
                      </Label>
                      <Textarea
                        id="description"
                        name="description"
                        required
                        value={formData.description}
                        onChange={handleChange}
                        className="mt-2 bg-zinc-950 border-white/10 text-white rounded-none focus:border-amber-500 min-h-[150px] resize-none"
                        placeholder="Tell us about your vision. What are you looking to achieve? Any specific details, timeline, or budget considerations we should know about?"
                        data-testid="quote-description-input" />

                    </div>
                  </div>
                </div>

                {/* Submit */}
                <div className="pt-6 border-t border-white/10">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-amber-500 text-black font-heading text-xl tracking-wide py-6 rounded-none hover:bg-amber-400 transition-all duration-200 disabled:opacity-50"
                    data-testid="quote-submit-btn">

                    {loading ? "SUBMITTING..." : "START THE CONVERSATION"}
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                  <p className="font-body text-sm text-zinc-500 text-center mt-4">
                    We typically respond within 24-48 business hours.
                  </p>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-zinc-950 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12">

            <h2 className="font-heading text-3xl text-white">WHAT TO EXPECT</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
            {
              step: "01",
              title: "REACH OUT",
              description: "Fill out the form with your project details. The more info you provide, the better."
            },
            {
              step: "02",
              title: "CONNECT",
              description: "We'll reach out to discuss your vision and answer any questions you have."
            },
            {
              step: "03",
              title: "PLAN",
              description: "Together we'll figure out the scope of work and next steps for your project."
            }].
            map((item, index) =>
            <motion.div
              key={item.step}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="text-center p-6">

                <div className="font-heading text-5xl text-amber-500 mb-2">{item.step}</div>
                <h3 className="font-heading text-xl text-white mb-2">{item.title}</h3>
                <p className="font-body text-sm text-zinc-400">{item.description}</p>
              </motion.div>
            )}
          </div>
        </div>
      </section>
    </div>);

}